import React, { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Dropdown,
    Field,
    Input,
    Option,
    Textarea,
} from '@fluentui/react-components';
import { DialogOpenChangeEventHandler } from '@fluentui/react-dialog';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useScrollBarStyle } from './scrollbar.styles';
import IConnectToTeam from '../../../../../model/IConnectToTeam';

export enum WelcomeMessageDialogMode {
    New = 'New',
    Edit = 'Edit',
}

const MAX_AIRCRAFT_REGISTRATION_LENGTH = 5;
const MAX_MESSAGE_LENGTH = 1000;

const normalizeFlight = (value: string): string => (value ?? '').trim().toUpperCase();

const getFlightValidationError = (
    value: string,
    t: (key: string) => string
): string | undefined => {
    const normalizedFlight = normalizeFlight(value);

    if (!normalizedFlight) {
        return undefined;
    }

    if (!/^G[A-Z]{0,4}$/.test(normalizedFlight)) {
        return t('CreateOrUpdateMessageDialog_FlightField_Invalid_Error');
    }

    return undefined;
};

interface CreateOrUpdateMessageDialogActionProps {
    open: boolean;
    mode: WelcomeMessageDialogMode;
    flight: string;
    welcomeMessage: string;
    autoConnectionEnabled: boolean;
    autoConnectionMessage: string;
    autoConnectionGroundTeamRowKey: string;
    allGroundTeams: IConnectToTeam[];
    loadStatus: string;
    onSaveBtnClick: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        data: {
            flight: string;
            welcomeMessage: string;
            autoConnectionEnabled: boolean;
            autoConnectionMessage: string;
            autoConnectionGroundTeamRowKey: string;
        }
    ) => void;
    onOpenChange?: DialogOpenChangeEventHandler;
}

const isDataValid = (
    flight: string,
    newFlight: string,
    welcomeMessage: string,
    newWelcomeMessage: string,
    autoConnectionEnabled: boolean,
    newAutoConnectionEnabled: boolean,
    autoConnectionMessage: string,
    newAutoConnectionMessage: string,
    groundTeam: string,
    newGroundTeam: string
): boolean => {
    const normalizedFlight = (newFlight ?? '').trim();
    const normalizedStoredFlight = (flight ?? '').trim().toUpperCase();
    const normalizedWelcomeMessage = (newWelcomeMessage ?? '').trim();
    const normalizedAutoConnectionMessage = (newAutoConnectionMessage ?? '').trim();
    const normalizedGroundTeam = (newGroundTeam ?? '').trim();

    if (!normalizedFlight) {
        return false;
    }

    if (!/^G[A-Z]{0,4}$/i.test(normalizedFlight)) {
        return false;
    }

    const hasChanges =
        normalizeFlight(normalizedFlight) !== normalizedStoredFlight ||
        normalizedWelcomeMessage !== (welcomeMessage ?? '').trim() ||
        newAutoConnectionEnabled !== autoConnectionEnabled ||
        normalizedAutoConnectionMessage !== (autoConnectionMessage ?? '').trim() ||
        normalizedGroundTeam !== (groundTeam ?? '').trim();

    if (!hasChanges) {
        return false;
    }

    if (newAutoConnectionEnabled && !normalizedAutoConnectionMessage) {
        return false;
    }

    if (newAutoConnectionEnabled && !normalizedGroundTeam) {
        return false;
    }

    return true;
};

const CreateOrUpdateMessageDialogAction = ({
    open,
    mode,
    flight,
    welcomeMessage,
    autoConnectionEnabled,
    autoConnectionMessage,
    autoConnectionGroundTeamRowKey,
    allGroundTeams,
    loadStatus,
    onSaveBtnClick,
    onOpenChange,
}: CreateOrUpdateMessageDialogActionProps) => {
    const { t } = useTranslation();

    const [newFlightValue, setNewFlightValue] = useState<string>('');
    const [newWelcomeMessageValue, setNewWelcomeMessageValue] =
        useState<string>('');
    const [newAutoConnectionEnabledValue, setNewAutoConnectionEnabledValue] =
        useState<boolean>(false);
    const [newAutoConnectionMessageValue, setNewAutoConnectionMessageValue] =
        useState<string>('');
    const [newAutoConnectionGroundTeamRowKeyValue, setNewAutoConnectionGroundTeamRowKeyValue] =
        useState<string>('');

    const scrollBarStyle = useScrollBarStyle();

    useEffect(() => {
        setNewFlightValue(flight);
        setNewWelcomeMessageValue(welcomeMessage);
        setNewAutoConnectionEnabledValue(autoConnectionEnabled);
        setNewAutoConnectionMessageValue(autoConnectionMessage);
        setNewAutoConnectionGroundTeamRowKeyValue(autoConnectionGroundTeamRowKey);
    }, [flight, welcomeMessage, autoConnectionEnabled, autoConnectionMessage, autoConnectionGroundTeamRowKey]);

    useEffect(() => {
        if (!open) {
            setNewFlightValue('');
            setNewWelcomeMessageValue('');
            setNewAutoConnectionEnabledValue(false);
            setNewAutoConnectionMessageValue('');
            setNewAutoConnectionGroundTeamRowKeyValue('');
        }
    }, [open]);

    const selectedGroundTeam = allGroundTeams.find(
        (team) => team.key === newAutoConnectionGroundTeamRowKeyValue
    );

    const flightValidationError = getFlightValidationError(newFlightValue, t);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {t('CreateOrUpdateMessageDialog_Title').replace(
                            '{mode}',
                            mode
                        )}
                    </DialogTitle>
                    <DialogContent className={scrollBarStyle}>
                        <Field
                            label={t(
                                'CreateOrUpdateMessageDialog_AircraftRegistrationNumber_Label'
                            )}
                            required
                            hint={t('CreateOrUpdateMessageDialog_FlightField_Hint')}
                            validationState={flightValidationError ? 'error' : undefined}
                            validationMessage={flightValidationError}
                        >
                            <Input
                                type="text"
                                maxLength={MAX_AIRCRAFT_REGISTRATION_LENGTH}
                                placeholder={
                                    t(
                                        'CreateOrUpdateMessageDialog_FlightField_Placeholder'
                                    ) ?? ''
                                }
                                disabled={
                                    mode === WelcomeMessageDialogMode.Edit
                                }
                                value={newFlightValue}
                                onChange={(_, data) => {
                                    setNewFlightValue(data.value);
                                }}
                            />
                        </Field>
                        <Field
                            label={t(
                                'CreateOrUpdateMessageDialog_WelcomeMessage_Label'
                            )}
                        >
                            <Textarea
                                appearance="outline"
                                resize="none"
                                maxLength={MAX_MESSAGE_LENGTH}
                                value={newWelcomeMessageValue}
                                onChange={(_, data) => {
                                    setNewWelcomeMessageValue(data.value);
                                }}
                                rows={5}
                            />
                        </Field>
                        <Checkbox
                            checked={newAutoConnectionEnabledValue}
                            label={t('CreateOrUpdateMessageDialog_Checkbox_AutoConnectionEnabled')}
                            onChange={(_, data) => {
                                setNewAutoConnectionEnabledValue(!!data.checked);
                            }}
                        />
                        <Field label={t('CreateOrUpdateMessageDialog_Field_AutoConnectionMessage')}>
                            <Textarea
                                appearance="outline"
                                resize="none"
                                maxLength={MAX_MESSAGE_LENGTH}
                                value={newAutoConnectionMessageValue}
                                onChange={(_, data) => {
                                    setNewAutoConnectionMessageValue(data.value);
                                }}
                                rows={3}
                            />
                        </Field>
                        <Field label={t('CreateOrUpdateMessageDialog_Field_GroundTeam')}>
                            <Dropdown
                                value={
                                    selectedGroundTeam
                                        ? `${selectedGroundTeam.teamName} - ${selectedGroundTeam.channelName}`
                                        : ''
                                }
                                disabled={loadStatus !== 'idle'}
                                placeholder={t('CreateOrUpdateMessageDialog_Placeholder_SelectGroundTeam')}
                                onOptionSelect={(_, data) => {
                                    setNewAutoConnectionGroundTeamRowKeyValue(
                                        data.optionValue ?? ''
                                    );
                                }}
                            >
                                {allGroundTeams.map((team) => (
                                    <Option key={team.key} value={team.key}>
                                        {`${team.teamName} - ${team.channelName}`}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={
                                !isDataValid(
                                    flight,
                                    newFlightValue,
                                    welcomeMessage,
                                    newWelcomeMessageValue,
                                    autoConnectionEnabled,
                                    newAutoConnectionEnabledValue,
                                    autoConnectionMessage,
                                    newAutoConnectionMessageValue,
                                    autoConnectionGroundTeamRowKey,
                                    newAutoConnectionGroundTeamRowKeyValue
                                )
                            }
                            onClick={(e) => {
                                onSaveBtnClick(e, {
                                    flight: normalizeFlight(newFlightValue),
                                    welcomeMessage: newWelcomeMessageValue,
                                    autoConnectionEnabled: newAutoConnectionEnabledValue,
                                    autoConnectionMessage: newAutoConnectionMessageValue,
                                    autoConnectionGroundTeamRowKey: newAutoConnectionGroundTeamRowKeyValue,
                                });
                            }}
                        >
                            {t('Button_Common_Save')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default CreateOrUpdateMessageDialogAction;